<div class="admin-messages-layout admin-page-wrap">
  <h1 class="page-title"><i class="bi bi-envelope me-2"></i> Messages</h1>
  <p class="page-subtitle">Manage contact messages from users. View inbox and sent, flag messages, and reply.</p>

  @if (error) {
    <div class="alert alert-danger">{{ error }}</div>
  }

  @if (loading) {
    <div class="panel messages-panel">
      <div class="panel-loading">
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
        Loading…
      </div>
    </div>
  } @else {
    <div class="messages-grid">
      <div class="panel messages-list-panel">
        <div class="panel-header">
          <div class="panel-header-left">
            <div class="inbox-toggle">
              <button type="button" class="inbox-btn" [class.active]="inboxView === 'inbox'" (click)="setInboxView('inbox')">Inbox</button>
              <button type="button" class="inbox-btn" [class.active]="inboxView === 'sent'" (click)="setInboxView('sent')">Sent</button>
            </div>
            @if (inboxView === 'inbox') {
              <div class="flag-filter">
                <div class="flag-buttons">
                  <button type="button" class="flag-btn flag-red" [class.active]="flagFilter === 'red'" (click)="setFlagFilter('red')" title="Red"><i class="bi bi-flag-fill"></i></button>
                  <button type="button" class="flag-btn flag-yellow" [class.active]="flagFilter === 'yellow'" (click)="setFlagFilter('yellow')" title="Yellow"><i class="bi bi-flag-fill"></i></button>
                  <button type="button" class="flag-btn flag-green" [class.active]="flagFilter === 'green'" (click)="setFlagFilter('green')" title="Green"><i class="bi bi-flag-fill"></i></button>
                </div>
                <button type="button" class="inbox-btn" [class.active]="unreadFilter" (click)="setUnreadFilter(!unreadFilter)" title="Unread">Unread</button>
              </div>
            }
          </div>
          <div class="panel-header-right">
            <button type="button" class="btn btn-sm btn-primary" (click)="openNewMessage()">
              <i class="bi bi-plus-lg me-1"></i> New Message
            </button>
            <span class="messages-count-text">{{ totalElements }} messages</span>
          </div>
        </div>
        <div class="panel-body p-0">
          <div class="messages-table-wrap">
            <table class="messages-table">
              <thead>
                <tr>
                  <th (click)="setSort('userName')" class="sortable" role="button" tabindex="0">
                    Username <i class="bi" [ngClass]="sortIcon('userName')"></i>
                  </th>
                  <th (click)="setSort('topic')" class="sortable" role="button" tabindex="0">
                    Topic <i class="bi" [ngClass]="sortIcon('topic')"></i>
                  </th>
                  <th (click)="setSort('createdAt')" class="sortable" role="button" tabindex="0">
                    Date <i class="bi" [ngClass]="sortIcon('createdAt')"></i>
                  </th>
                  @if (inboxView === 'inbox') {
                    <th class="text-center col-flag">Flag</th>
                  }
                  <th class="text-center col-actions"></th>
                </tr>
              </thead>
              <tbody>
                @for (msg of sortedMessages; track msg.id) {
                  <tr
                    class="message-row"
                    [class.selected]="isInThread(msg.id)"
                    [class.unread]="inboxView === 'inbox' && !msg.readAt"
                    (click)="selectMessage(msg)"
                    role="button"
                    tabindex="0">
                    <td>
                      <span [title]="getNameTooltip(msg)">
                        {{ inboxView === 'sent' ? (msg.recipientUsername || '—') : (msg.userUsername || '—') }}
                      </span>
                    </td>
                    <td>{{ msg.topic }}</td>
                    <td>{{ formatDate(msg.createdAt) }}</td>
                    @if (inboxView === 'inbox') {
                      <td class="text-center" (click)="$event.stopPropagation()">
                        <button
                          type="button"
                          class="btn btn-sm btn-link p-0 flag-cell-btn"
                          [class.text-danger]="msg.receiverFlag === 'red'"
                          [class.text-warning]="msg.receiverFlag === 'yellow'"
                          [class.text-success]="msg.receiverFlag === 'green'"
                          [class.text-muted]="!msg.receiverFlag"
                          (click)="cycleFlag(msg, $event)"
                          [title]="'Flag: ' + (msg.receiverFlag || 'none')">
                          <i class="bi" [ngClass]="msg.receiverFlag ? 'bi-flag-fill' : 'bi-flag'"></i>
                        </button>
                      </td>
                    }
                    <td (click)="$event.stopPropagation()">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        (click)="openDeleteConfirm(msg, $event)"
                        title="Delete">
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (sortedMessages.length === 0) {
            <p class="messages-empty">No messages.</p>
          }
          @if (totalPages > 1) {
            <nav class="messages-pagination">
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="page === 0" (click)="goToPage(page - 1)">
                «
              </button>
              <span class="pagination-info">Page {{ page + 1 }} of {{ totalPages }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="page >= totalPages - 1" (click)="goToPage(page + 1)">
                »
              </button>
            </nav>
          }
        </div>
      </div>

      <div class="panel messages-detail-panel">
        @if (selectedLoading) {
          <div class="panel-loading">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Loading…
          </div>
        } @else if (threadMessages.length > 0) {
          <div class="thread-header">
            <span class="thread-title">Thread ({{ threadMessages.length }} message{{ threadMessages.length !== 1 ? 's' : '' }})</span>
            <button type="button" class="btn btn-sm btn-primary" (click)="openReply()">
              <i class="bi bi-reply me-1"></i> Reply
            </button>
          </div>
          <div class="thread-body">
            @for (m of threadMessages; track m.id) {
              <div class="thread-message" [class.last]="$last">
                <dl class="thread-meta">
                  <dt>{{ m.recipientUserId ? 'To' : 'From' }}</dt>
                  <dd>
                    <span [title]="getNameTooltip(m)">
                      {{ m.recipientUserId ? m.recipientUsername : m.userUsername }}
                    </span>
                  </dd>
                  <dt>Date</dt>
                  <dd>{{ formatDate(m.createdAt) }}</dd>
                </dl>
                <div class="message-html message-content" [innerHTML]="m.messageText | safeHtml"></div>
                @if (m.hasAttachment) {
                  <p class="attachment-link mt-2 mb-0">
                    <button type="button" class="btn btn-link btn-sm p-0 text-decoration-none" (click)="downloadAttachment(m)">
                      <i class="bi bi-download me-1"></i>{{ m.attachmentFilename || 'attachment' }}
                    </button>
                  </p>
                }
              </div>
            }
          </div>
        } @else {
          <div class="messages-placeholder">
            <i class="bi bi-envelope-open d-block mb-2"></i>
            <p class="text-muted mb-0">Select a message to view and reply</p>
          </div>
        }
      </div>
    </div>
  }

  <!-- Delete confirm modal -->
  @if (deleteConfirm) {
    <div class="modal-overlay" (click)="cancelDelete()">
      <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Delete message</h5>
            <button type="button" class="btn-close" (click)="cancelDelete()"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this message {{ deleteConfirm.recipientUserId ? ('to ' + (deleteConfirm.recipientUserName || '')) : ('from ' + (deleteConfirm.userName || '')) }}?</p>
            <p class="text-muted small mb-0">{{ inboxView === 'inbox' ? 'It will be removed from your inbox.' : 'It will be removed from your sent folder.' }} {{ (inboxView === 'inbox' && deleteConfirm.senderDeleted) || (inboxView === 'sent' && deleteConfirm.receiverDeleted) ? 'The message will be permanently deleted.' : '' }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="cancelDelete()">Cancel</button>
            <button type="button" class="btn btn-danger" (click)="confirmDelete()" [disabled]="deleteInProgress">
              @if (deleteInProgress) { Deleting… } @else { Delete }
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Compose modal (New Message / Reply) -->
  @if (composeModal) {
    <div class="modal-overlay">
      <div class="modal-dialog compose-modal-dialog" (click)="$event.stopPropagation()"
        [style.top.px]="composeModalTop"
        [style.left.px]="composeModalLeft"
        (mousedown)="$event.stopPropagation()">
        <div class="modal-content">
          <div class="modal-header compose-drag-handle cursor-move" (mousedown)="onComposeDragStart($event)">
            <h5 class="modal-title">{{ composeMode === 'reply' ? 'Reply' : 'New Message' }}</h5>
            <button type="button" class="btn-close" (click)="requestCloseCompose()"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-semibold">Recipient</label>
              @if (composeMode === 'reply') {
                <input type="text" class="form-control" [value]="newMessageRecipientQuery" readonly disabled />
              } @else {
                <div class="position-relative recipient-search-wrap">
                  <input
                    type="text"
                    class="form-control"
                    [(ngModel)]="newMessageRecipientQuery"
                    (input)="onRecipientInput()"
                    (focus)="onRecipientInput()"
                    placeholder="Search by username, first name or last name (min 2 chars)"
                    autocomplete="off" />
                  @if (newMessageRecipient) {
                    <span class="position-absolute top-50 end-0 translate-middle-y me-2">
                      <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearRecipient()"><i class="bi bi-x"></i></button>
                    </span>
                  }
                  @if (newMessageRecipientSearching) {
                    <span class="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" style="pointer-events: none;"><span class="spinner-border spinner-border-sm"></span></span>
                  }
                  @if (newMessageRecipientQuery.length >= 2 && !newMessageRecipient) {
                    <ul class="list-group recipient-dropdown">
                      @if (newMessageRecipientSearching) {
                        <li class="list-group-item text-muted">Searching…</li>
                      } @else if (newMessageRecipientResults.length === 0) {
                        <li class="list-group-item text-muted">No users found</li>
                      } @else {
                        @for (u of newMessageRecipientResults; track u.id) {
                          <li class="list-group-item list-group-item-action" (click)="selectRecipient(u)">
                            {{ u.displayName }} <span class="text-muted">({{ u.username }})</span>
                          </li>
                        }
                      }
                    </ul>
                  }
                </div>
              }
            </div>
            <div class="mb-3">
              <label for="newMessageTopic" class="form-label fw-semibold">Topic</label>
              <input id="newMessageTopic" type="text" class="form-control" [(ngModel)]="newMessageTopic" placeholder="Message topic" />
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Message</label>
              @if (newMessageEditor) {
                <ngx-editor-menu [editor]="newMessageEditor"></ngx-editor-menu>
                <ngx-editor [editor]="newMessageEditor" [(ngModel)]="newMessageHtml" outputFormat="html" placeholder="Type your message…"></ngx-editor>
              }
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Attachment</label>
              <input type="file" class="form-control" (change)="onAttachmentChange($event)" />
              <small class="text-muted">Max {{ maxAttachmentSizeMb }} MB</small>
              @if (newMessageAttachment) {
                <p class="mb-0 mt-1 small"><i class="bi bi-paperclip"></i> {{ newMessageAttachment.name }}</p>
              }
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="requestCloseCompose()">Cancel</button>
            <button type="button" class="btn btn-primary" (click)="showComposeSendConfirm()"
              [disabled]="!canSendCompose || newMessageSending">
              @if (newMessageSending) { Sending… } @else { {{ composeMode === 'reply' ? 'Send reply' : 'Send' }} }
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Compose cancel confirm -->
  @if (composeCancelConfirm) {
    <div class="modal-overlay modal-overlay-high" (click)="cancelComposeConfirmClose()">
      <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Discard message?</h5>
            <button type="button" class="btn-close" (click)="cancelComposeConfirmClose()"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">Your message has not been sent. Discard it?</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="cancelComposeConfirmClose()">Keep editing</button>
            <button type="button" class="btn btn-outline-danger" (click)="confirmCloseCompose()">Discard</button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Compose send confirm -->
  @if (composeSendConfirm) {
    <div class="modal-overlay modal-overlay-high" (click)="cancelComposeSend()">
      <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ composeMode === 'reply' ? 'Send reply' : 'Send message' }}</h5>
            <button type="button" class="btn-close" (click)="cancelComposeSend()"></button>
          </div>
          <div class="modal-body">
            @if (newMessageSendError) {
              <div class="alert alert-danger mb-3">{{ newMessageSendError }}</div>
            }
            <p>{{ composeMode === 'reply' ? ('Send this reply to ' + replyToName + '?') : ('Send this message to ' + (newMessageRecipient?.displayName || '') + '?') }}</p>
            <div class="message-html message-preview" [innerHTML]="newMessageHtml | safeHtml"></div>
            @if (newMessageAttachment) {
              <p class="mt-2 mb-0 small"><i class="bi bi-paperclip"></i> {{ newMessageAttachment.name }}</p>
            }
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="cancelComposeSend()">Cancel</button>
            <button type="button" class="btn btn-primary" (click)="confirmSendCompose()" [disabled]="newMessageSending">
              @if (newMessageSending) { Sending… } @else { Send }
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</div>
